// held-target-move.ts — the caster moves a creature it HOLDS (2026-10-07; Geno's "Psion Subclass Revisions And Changes v12", Mind of the
// Telekinetic L7 "Cogni Puppet Strings" — "at the start of each of your turns, you may move it up to 20 ft within range" — and L9 "World in
// Motion" — "you may also move one creature that failed this roll up to 30 ft anywhere within the Sphere"). One generic layer type:
//
//   caster_may_move_target_each_turn_ft  (int)  — on the APPLYING item (the menta). On the caster's turn, a creature holding a live status
//                                                  row this caster applied from that item (status_caster_id + status_origin_id) may be moved
//                                                  by the caster up to N ft, as a Force Move (no AP, no movement, collisions ignored — the
//                                                  map Force Move's landing / zone / fall / reach rules), never out of the item's range band
//                                                  measured from the caster.
//
// ONE move (or skip) per caster turn (World in Motion: "one creature"; Puppet Strings holds one creature anyway). The spend is client-side
// encounter state like Dodge's: the acting page puts the layerless marker status "Telekinetic Hold Used" on the CASTER (ends at the start of
// its next turn; status_connected_id names the turn) through the Dodge lane — the caster's revision CAS answers a second page stale — and
// every page reads it off the caster's own status rows (heldTargetMoveSpentThisTurn). The owner prefers prompting
// players over auto-triggers, so the page OFFERS the move at the start of the caster's turn (websocket/held-target-move.ts) and the player picks
// the destination or skips. A move that would strip the very status that grants it (a zone-held creature carried out of the zone — World in
// Motion's Sphere) is refused: the force move's own end-of-move zone sweep names it (zone_exits).
// The grip itself is NOT a transport link here: the holder stays where it is (the map Force Move would drag a grapplee's grappler along).
// Browser rule (front-end rule, CLAUDE.md): PHP only proves the authority (held_target_move_authority.php — the target row's status, the
// caster's turn); it never re-judges the distance or the once-per-turn cap. Pure: every read goes through the injected readers.

import { isSelfStatusOrigin } from '../statuses/status-origin.js';

type Row = Record<string, unknown>;

export const CASTER_MAY_MOVE_TARGET_EACH_TURN_FT = 'caster_may_move_target_each_turn_ft';

/** RangeType x_distance (includes/additions.php RangeType::LIST — the range-check.ts table). Self / Reach / unknown = no band bound. */
const RANGE_FT: Record<string, number> = {
  '4C761395-2532-3F23-7CBA-CA81AF5C0855': 30, 'A95F219A-6288-6B17-6DB7-03DE4B97AE96': 60, 'D1E8DA38-61C2-97AA-6173-4BF6157FEEE3': 120,
  'F6DAE84B-CBB3-5C0F-89EF-370DE841DFDA': 240,
};
/** Persisted coordinates are DECIMAL(…,3); a pick on the ring's edge must not fail on rounding. */
const EPSILON_FT = 0.05;

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? scalar(value[0]) : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const rows = (value: unknown): Row[] => (Array.isArray(value) ? value : Object.values(record(value) ?? {})).map(record).filter((r): r is Row => r !== null);
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

export interface HeldTargetMoveReaders {
  /** layer_value() */
  value: (row: Row, key: string) => unknown;
  /** The FULL definition of an item id (items + item_layer_types); null when the page lacks it. */
  definition: (id: string) => Row | null;
  /** A row's own TARGET status rows (the movement scope reader). */
  statuses: (row: Row) => Row[];
  /** [x, y, z] in feet (x / y floor plane, z the feet plane). */
  position: (row: Row) => [number, number, number];
}

export interface HeldTargetMoveOffer {
  casterId: string;
  originId: string;
  originName: string;
  targetId: string;
  targetName: string;
  /** How far the held creature may be moved (ft). */
  ft: number;
  /** The item's range band in ft (the destination stays within it, measured from the caster); null = no band bound. */
  rangeFt: number | null;
  /** The live status rows (this caster, this item) on the held creature — a move that strips any of them is refused. */
  statusIds: string[];
}

const ecOf = (row: Row): Row => record(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
const nameOf = (row: Row): string => String(scalar(ecOf(row).encounter_creature_name) ?? scalar(record(row.items)?.item_name) ?? 'creature');
const deadOf = (row: Row): boolean => Number(scalar(ecOf(row).encounter_creature_dead) ?? 0) === 1;

/** The move this item grants (0 = none). */
export function heldTargetMoveFt(definition: Row | null, read: Pick<HeldTargetMoveReaders, 'value'>): number {
  if (!definition) return 0;
  const n = Math.trunc(Number(scalar(read.value(definition, CASTER_MAY_MOVE_TARGET_EACH_TURN_FT))));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Every held-creature move the caster may make now: one per (item, held creature), the item's first live granting row. `spent` = the
 *  items already used (or skipped) this turn — their offers are withheld. */
export function heldTargetMoveOffers(encounter: Row, casterId: string, read: HeldTargetMoveReaders, spent: ReadonlySet<string> = new Set()): HeldTargetMoveOffer[] {
  const caster = upper(casterId);
  if (!UUID.test(caster)) return [];
  const offers = new Map<string, HeldTargetMoveOffer>();
  const definitions = new Map<string, Row | null>();
  for (const row of rows(encounter.encounter_creatures_arr)) {
    const targetId = idOf(row);
    if (!targetId || targetId === caster || deadOf(row)) continue;
    for (const status of read.statuses(row)) {
      if (upper(status.status_caster_id) !== caster) continue;
      if (Number(scalar(status.status_deleted) ?? 0) === 1) continue;
      const originId = upper(status.status_origin_id);
      if (!UUID.test(originId) || spent.has(originId) || isSelfStatusOrigin(upper(status.status_id), originId)) continue;
      const key = `${originId}|${targetId}`;
      const held = offers.get(key);
      if (held) { held.statusIds.push(upper(status.status_id)); continue; }
      if (!definitions.has(originId)) definitions.set(originId, read.definition(originId));
      const definition = definitions.get(originId) ?? null;
      const ft = heldTargetMoveFt(definition, read);
      if (ft <= 0) continue;
      offers.set(key, { casterId: caster, originId, originName: String(scalar(record(definition!.items)?.item_name) ?? 'Ability'), targetId,
        targetName: nameOf(row), ft, rangeFt: RANGE_FT[upper(read.value(definition!, 'range'))] ?? null, statusIds: [upper(status.status_id)] });
    }
  }
  const out = [...offers.values()];
  return out;
}

export type HeldTargetMoveVerdict = { ok: true } | { ok: false; reason: 'too-far' | 'out-of-range' | 'no-position'; message: string };

/** The page's own check of a picked destination: within N ft of where the held creature stands, and within the item's range band from the
 *  caster. Centre-to-centre feet (the persisted positions); the landing itself is the force move's. */
export function heldTargetMoveCheck(offer: HeldTargetMoveOffer, casterAt: readonly number[], targetAt: readonly number[],
  destination: { x: number; y: number; z: number }): HeldTargetMoveVerdict {
  const finite = (p: readonly number[]): boolean => p.length >= 3 && p.slice(0, 3).every(Number.isFinite);
  if (!finite(casterAt) || !finite(targetAt) || ![destination.x, destination.y, destination.z].every(Number.isFinite))
    return { ok: false, reason: 'no-position', message: `${offer.originName}: the page does not hold a position for this move.` };
  const moved = Math.hypot(destination.x - targetAt[0]!, destination.y - targetAt[1]!, destination.z - targetAt[2]!);
  if (moved > offer.ft + EPSILON_FT)
    return { ok: false, reason: 'too-far', message: `${offer.originName}: ${offer.targetName} can be moved at most ${offer.ft} ft (that spot is ${Math.round(moved)} ft away).` };
  if (offer.rangeFt !== null) {
    const reach = Math.hypot(destination.x - casterAt[0]!, destination.y - casterAt[1]!, destination.z - casterAt[2]!);
    if (reach > offer.rangeFt + EPSILON_FT)
      return { ok: false, reason: 'out-of-range', message: `${offer.originName}: the destination must stay within ${offer.rangeFt} ft of you.` };
  }
  return { ok: true };
}

/** The once-per-turn key: one move (or skip) per item per caster turn. */
export function heldTargetMoveTurnKey(encounterId: string, casterId: string, round: unknown, originId: string): string {
  return `${upper(encounterId)}|${upper(casterId)}|${String(scalar(round) ?? '')}|${upper(originId)}`;
}

/** A force move's end-of-move zone strips would remove the status that grants the move (the creature left the zone that holds it). */
export function heldTargetMoveStripsGrant(offer: Pick<HeldTargetMoveOffer, 'statusIds'>, strips: ReadonlyArray<{ statusId: string }> | undefined): boolean {
  return (strips ?? []).some(strip => offer.statusIds.includes(upper(strip.statusId)));
}

// ── The once-per-turn spend (2026-10-08, owner: the front end calculates from state it holds; the backend lazy-saves) ──────────────────────
/** "Telekinetic Hold Used" — the engine marker status (no layers; minted by tools/probes/held_target_move_marker_mint_v1.php). */
export const HELD_TARGET_MOVE_MARKER_ID = 'F7B1C2D3-5A4E-4C61-9B8A-0D2E3F4A5B6C';

/** The turn every page names the same way, from the encounter's AUTHORITATIVE turn scalars (encounter-stats-update.ts FT_ENC_SCALAR_AUTH_KEYS):
 *  the combat, the round, the creature whose turn it is, and — in a Legendary interlude — the main turn it interrupts (two legendary turns of one creature
 *  in a round follow different main turns). A creature's main turn comes once a round. */
export function heldTargetMoveTurnToken(field: (key: string) => unknown): string {
  const legendary = String(scalar(field('turn_phase')) ?? '').trim().toLowerCase() === 'legendary';
  const round = Math.trunc(Number(scalar(field('current_round')) ?? 0)) || 0;
  // + the combat (a later combat at the same round / holder is a new turn — the spend marker is named by this token)
  return [upper(field('active_combat_id')), round, legendary ? 'L' : 'M', upper(field('current_encounter_creature_id_turn')),
    legendary ? upper(field('pending_main_turn_encounter_creature_id')) : ''].join('|');
}

/** A stable UUID-shaped id from a string (cyrb128 — every page derives the same one; no crypto, no network). */
function stableUuid(text: string): string {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0; i < text.length; i++) {
    const k = text.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067); h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213); h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067); h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213); h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4; h2 ^= h1; h3 ^= h1; h4 ^= h1;
  const hex = [h1, h2, h3, h4].map(h => (h >>> 0).toString(16).padStart(8, '0')).join('').toUpperCase();
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-${'89AB'[parseInt(hex[16]!, 16) & 3]}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/** The marker's status_connected_id for this caster's turn — the turn token, made a UUID. */
export function heldTargetMoveSpendConnectedId(casterId: string, turn: string): string {
  return stableUuid(`held-target-move|${upper(casterId)}|${turn}`);
}

/** Has this caster spent its held move THIS turn? Its own status rows (the page's encounter state) hold the marker named for this turn.
 *  Per caster + turn = per caster + item + turn: the layer's items (Cogni Puppet Strings, World in Motion) are concentration, so a caster
 *  holds creatures through one of them at a time — and one marker row per caster is all the statuses table keeps (unique target + item).
 *  A recast of the item in the same turn stays spent; a legendary turn or a later combat names a new turn. */
export function heldTargetMoveSpentThisTurn(casterStatuses: readonly Row[], casterId: string, turn: string): boolean {
  if (turn === '') return false;
  const connected = heldTargetMoveSpendConnectedId(casterId, turn);
  return casterStatuses.some(status => upper(status.status_item_id) === HELD_TARGET_MOVE_MARKER_ID && upper(status.status_connected_id) === connected
    && Number(scalar(status.status_deleted) ?? 0) !== 1);
}
